import { useInView, UseInViewOptions } from "framer-motion";
import { useIsMobile } from "./use-mobile";
import { RefObject } from "react";

interface UseMobileOnceInViewOptions {
  margin?: UseInViewOptions["margin"];
  once?: boolean;
}

/**
 * Custom hook that wraps useInView with mobile-optimized behavior.
 * On mobile: animations trigger once and stay (cleaner UX)
 * On desktop: animations can re-trigger on scroll (current behavior)
 */
export const useMobileOnceInView = (
  ref: RefObject<Element | null>,
  options: UseMobileOnceInViewOptions = {}
) => {
  const isMobile = useIsMobile();
  const { margin = "-100px", once = false } = options;
  
  // On mobile, always use once: true for cleaner animations
  // On desktop, use the provided once value (default false for re-triggering)
  // Default to once: true when isMobile is still undefined (first render)
  const shouldOnce = true;
  const isInView = useInView(ref, { 
    once: shouldOnce, 
    margin 
  });
  
  return isInView;
};

/**
 * Returns viewport config for framer-motion whileInView animations.
 * On mobile: once: true for cleaner UX
 * On desktop: once: false for re-triggering animations
 */
export const useMobileOnceViewport = (margin: string = "-100px") => {
  const isMobile = useIsMobile();
  // Default to once: true when isMobile is still undefined (SSR/first render)
  // This prevents framer-motion from locking in once: false before the media query resolves
  const shouldOnce = true;
  return {
    once: shouldOnce,
    margin,
  };
};
